import React, { useState, useEffect } from 'react';
import { RealtimeDBService, CourseEnrollmentService } from '../../firebase/unifiedDataService';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { Logger } from '../../utils/logger';
// Note: getCourseStats should be added to unifiedDataService
import { getCourseStats } from '../../firebase/firestore';

const log = Logger.create('CourseLeaderboardTab');

/**
 * CourseLeaderboardTab - Shows course leaderboard based on student performance
 */
export default function CourseLeaderboardTab({ course, courseId }) {
  const { user } = useUnifiedAuth();
  
  const [leaderboard, setLeaderboard] = useState([]);
  const [timeRange, setTimeRange] = useState('all'); // all, month, week
  const [sortBy, setSortBy] = useState('score'); // score, quizzes, streak
  const [isLoading, setIsLoading] = useState(true);
  
  // Load leaderboard data
  useEffect(() => {
    const loadLeaderboard = async () => {
      if (!user || !courseId) return;
      
      setIsLoading(true);
      
      try {
        // Get all enrollments for this course using CourseEnrollmentService
        const enrollments = await CourseEnrollmentService.getEnrollments(user.uid, courseId);
        
        if (enrollments.length === 0) {
          setLeaderboard([]);
          setIsLoading(false);
          return;
        }
        
        // Convert to object format for compatibility
        const enrollmentsMap = {};
        enrollments.forEach(e => { enrollmentsMap[e.studentId] = e; });
        const studentIds = Object.keys(enrollmentsMap);
        
        // Get stats for each student (Firestore with fallback to Realtime DB)
        const statsPromises = studentIds.map(async (studentId) => {
          const enrollment = enrollmentsMap[studentId];
          
          try {
            // Try Firestore first
            const stats = await getCourseStats(studentId, courseId);
            
            if (stats) {
              return {
                studentId,
                studentName: enrollment.studentName || 'Unbekannt',
                studentEmail: enrollment.studentEmail,
                enrolledAt: enrollment.enrolledAt,
                quizzesTaken: stats.quizzesTaken || 0,
                questionsAnswered: stats.questionsAnswered || 0,
                correctAnswers: stats.correctAnswers || 0,
                averageScore: stats.averageScore || 0,
                lastQuizDate: stats.lastQuizDate
              };
            }
          } catch (error) {
            log.warn(`Failed to fetch Firestore stats for ${studentId}, trying Realtime DB`, error);
          }
          
          // Fallback to Realtime DB using RealtimeDBService
          try {
            const stats = await RealtimeDBService.get(`student_stats/${studentId}/courseStats/${courseId}`);
            
            if (stats) {
              return {
                studentId,
                studentName: enrollment.studentName || 'Unbekannt',
                studentEmail: enrollment.studentEmail,
                enrolledAt: enrollment.enrolledAt,
                quizzesTaken: stats.quizzesTaken || 0,
                questionsAnswered: stats.questionsAnswered || 0,
                correctAnswers: stats.correctAnswers || 0,
                averageScore: stats.averageScore || 0,
                lastQuizDate: stats.lastQuizDate
              };
            }
          } catch (error) {
            log.error(`Failed to fetch Realtime DB stats for ${studentId}`, error);
          }
          
          // Return default if no stats found
          return {
            studentId,
            studentName: enrollment.studentName || 'Unbekannt',
            studentEmail: enrollment.studentEmail,
            enrolledAt: enrollment.enrolledAt,
            quizzesTaken: 0,
            questionsAnswered: 0,
            correctAnswers: 0,
            averageScore: 0,
            lastQuizDate: null
          };
        });
        
        const stats = await Promise.all(statsPromises);
        
        // Filter by time range
        let filteredStats = stats;
        const now = Date.now();
        
        if (timeRange === 'week') {
          const weekAgo = now - 7 * 24 * 60 * 60 * 1000;
          filteredStats = stats.filter(s => s.lastQuizDate && s.lastQuizDate > weekAgo);
        } else if (timeRange === 'month') {
          const monthAgo = now - 30 * 24 * 60 * 60 * 1000;
          filteredStats = stats.filter(s => s.lastQuizDate && s.lastQuizDate > monthAgo);
        }
        
        // Sort
        filteredStats.sort((a, b) => {
          if (sortBy === 'score') {
            return b.averageScore - a.averageScore;
          } else if (sortBy === 'quizzes') {
            return b.quizzesTaken - a.quizzesTaken;
          } else if (sortBy === 'correct') {
            const rateA = a.questionsAnswered > 0 ? (a.correctAnswers / a.questionsAnswered) : 0;
            const rateB = b.questionsAnswered > 0 ? (b.correctAnswers / b.questionsAnswered) : 0;
            return rateB - rateA;
          }
          return 0;
        });
        
        setLeaderboard(filteredStats);
        
      } catch (err) {
        log.error('Error loading leaderboard', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadLeaderboard();
  }, [user, courseId, timeRange, sortBy]);
  
  // Get rank display (removed medal emojis)
  const getMedal = (rank) => {
    return `${rank}.`;
  };
  
  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit'
    });
  };
  
  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            Rangliste
          </h2>
          
          <div className="flex items-center gap-3">
            {/* Time Range Filter */}
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">Gesamt</option>
              <option value="month">Letzter Monat</option>
              <option value="week">Letzte Woche</option>
            </select>
            
            {/* Sort By */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="score">Nach Score</option>
              <option value="quizzes">Nach Quizzes</option>
              <option value="correct">Nach Richtig-Rate</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Leaderboard Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            Lade Rangliste...
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="p-8 text-center">
            <svg className="w-16 h-16 mx-auto mb-2 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-gray-600 dark:text-gray-400">
              Noch keine Daten vorhanden.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              Die Rangliste füllt sich, wenn Studierende Quizze absolvieren.
            </p>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            {leaderboard.length >= 3 && (
              <div className="p-6 bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-end justify-center gap-4">
                  {/* 2nd Place */}
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center text-2xl mb-2 font-bold text-gray-700 dark:text-gray-200">
                      2
                    </div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate max-w-[100px]">
                      {leaderboard[1]?.studentName}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {Math.round(leaderboard[1]?.averageScore || 0)}%
                    </p>
                  </div>
                  
                  {/* 1st Place */}
                  <div className="text-center -mb-4">
                    <div className="w-20 h-20 mx-auto bg-yellow-400 dark:bg-yellow-600 rounded-full flex items-center justify-center text-3xl mb-2 shadow-lg font-bold text-yellow-900 dark:text-yellow-100">
                      1
                    </div>
                    <p className="font-bold text-gray-900 dark:text-white truncate max-w-[120px]">
                      {leaderboard[0]?.studentName}
                    </p>
                    <p className="text-sm text-yellow-600 dark:text-yellow-400 font-semibold">
                      {Math.round(leaderboard[0]?.averageScore || 0)}%
                    </p>
                  </div>
                  
                  {/* 3rd Place */}
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto bg-amber-300 dark:bg-amber-700 rounded-full flex items-center justify-center text-2xl mb-2 font-bold text-amber-900 dark:text-amber-100">
                      3
                    </div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate max-w-[100px]">
                      {leaderboard[2]?.studentName}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {Math.round(leaderboard[2]?.averageScore || 0)}%
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Full List */}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {leaderboard.map((student, index) => (
                <div 
                  key={student.studentId} 
                  className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 theme-transition ${
                    index < 3 ? 'bg-yellow-50/50 dark:bg-yellow-900/10' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Rank */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                      index === 1 ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300' :
                      index === 2 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                      'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}>
                      {getMedal(index + 1)}
                    </div>
                    
                    {/* Student Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {student.studentName}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {student.quizzesTaken} Quizze • {student.questionsAnswered} Fragen
                      </p>
                    </div>
                    
                    {/* Stats */}
                    <div className="text-right">
                      <p className={`text-lg font-bold ${
                        student.averageScore >= 80 ? 'text-green-600 dark:text-green-400' :
                        student.averageScore >= 50 ? 'text-yellow-600 dark:text-yellow-400' :
                        'text-gray-600 dark:text-gray-400'
                      }`}>
                        {Math.round(student.averageScore)}%
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        {student.questionsAnswered > 0 
                          ? `${Math.round((student.correctAnswers / student.questionsAnswered) * 100)}% richtig`
                          : '-'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
